import { useRef, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ArrowLeft, Building2, Pencil, MapPin, ShieldCheck, Upload, Download, Trash2,
  AlertTriangle, CheckCircle2, XCircle,
} from "lucide-react";
import { api, API, apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrency, fmtPct, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const DUE_DILIGENCE_CATEGORY = "due_diligence";
const DD_SEVERITY_META = {
  ok: { icon: CheckCircle2, color: "hsl(var(--success))" },
  warning: { icon: AlertTriangle, color: "hsl(var(--warning))" },
  critical: { icon: XCircle, color: "hsl(var(--destructive))" },
};

export default function PropertyDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});

  const { data, isLoading } = useQuery({
    queryKey: ["property", id],
    queryFn: async () => (await api.get(`/properties/${id}`)).data,
  });
  const { data: regions = [] } = useQuery({
    queryKey: ["market-regions"], queryFn: async () => (await api.get("/market/regions")).data.regions,
  });

  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const { data: allDocuments = [] } = useQuery({
    queryKey: ["documents"], queryFn: async () => (await api.get("/documents")).data,
  });
  const ddDocuments = allDocuments.filter((d) => d.property_id === id && d.category === DUE_DILIGENCE_CATEGORY);
  const { data: ddReports = [] } = useQuery({
    queryKey: ["due-diligence-reports", id],
    queryFn: async () => (await api.get(`/due-diligence/${id}/reports`)).data,
  });

  const removeDoc = useMutation({
    mutationFn: (docId) => api.delete(`/documents/${docId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documents"] }),
  });

  const onDdFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", DUE_DILIGENCE_CATEGORY);
      fd.append("property_id", id);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      qc.invalidateQueries({ queryKey: ["documents"] });
      toast.success("OK");
    } catch (err) {
      toast.error(apiErrorMessage(err?.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const analyzeDd = useMutation({
    mutationFn: () => api.post(`/due-diligence/${id}/analyze`, null, { params: { lang } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["due-diligence-reports", id] });
      toast.success("OK");
    },
    onError: (e) => toast.error(apiErrorMessage(e?.response?.data?.detail)),
  });

  useEffect(() => {
    if (data?.property) setForm(data.property);
  }, [data]);

  const update = useMutation({
    mutationFn: (payload) => api.put(`/properties/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["property", id] });
      qc.invalidateQueries({ queryKey: ["properties"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setEditOpen(false);
      toast.success("OK");
    },
    onError: () => toast.error("Error"),
  });

  const saveEdit = () => {
    update.mutate({
      name: form.name,
      address: form.address || "",
      property_type: form.property_type || "apartment",
      area: parseFloat(form.area) || 0,
      rooms: parseInt(form.rooms) || 0,
      purchase_price: parseFloat(form.purchase_price) || 0,
      current_value: parseFloat(form.current_value) || 0,
      status: form.status || "active",
      region: form.region || null,
    });
  };

  if (isLoading || !data) return <div className="h-64 flex items-center justify-center text-muted-foreground">…</div>;
  const { property: p, contracts, expenses, metrics } = data;

  return (
    <div className="space-y-6" data-testid="property-detail-page">
      <Button variant="ghost" onClick={() => navigate("/properties")} data-testid="back-btn" className="gap-2 -ml-2">
        <ArrowLeft className="h-4 w-4" /> {t("back")}
      </Button>

      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-sm bg-secondary flex items-center justify-center">
            <Building2 className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-display text-4xl">{p.name}</h1>
            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
              <MapPin className="h-3 w-3" /> {p.address || "—"}
            </p>
          </div>
        </div>
        <Button onClick={() => setEditOpen(true)} data-testid="edit-property-btn" className="rounded-sm gap-2">
          <Pencil className="h-4 w-4" /> {t("edit")}
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          [t("current_value"), fmtCurrency(p.current_value || p.purchase_price)],
          [t("gross_yield"), fmtPct(metrics.gross_yield)],
          [t("net_yield"), fmtPct(metrics.net_yield)],
          [t("monthly_rent"), fmtCurrency(metrics.annual_rent / 12)],
        ].map(([label, val], i) => (
          <motion.div key={label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Card className="p-6 rounded-sm border-border">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
              <p className="font-display text-2xl tabular-nums mt-2">{val}</p>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-sm border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border"><h3 className="text-sm uppercase tracking-wider text-muted-foreground">{t("related_contracts")}</h3></div>
          {contracts.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6">{t("empty_generic")}</p>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>{t("tenant")}</TableHead><TableHead className="text-right">{t("rent_amount")}</TableHead><TableHead>{t("status")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {contracts.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.tenant_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtCurrency(c.rent_amount)}</TableCell>
                    <TableCell><Badge className="rounded-sm bg-[hsl(var(--success))] text-white">{c.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        <Card className="rounded-sm border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border"><h3 className="text-sm uppercase tracking-wider text-muted-foreground">{t("related_expenses")}</h3></div>
          {expenses.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6">{t("empty_generic")}</p>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>{t("category")}</TableHead><TableHead>{t("date")}</TableHead><TableHead className="text-right">{t("amount")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {expenses.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell><Badge variant="secondary" className="rounded-sm">{e.category}</Badge></TableCell>
                    <TableCell>{fmtDate(e.date)}</TableCell>
                    <TableCell className="text-right tabular-nums text-[hsl(var(--destructive))]">-{fmtCurrency(e.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      <Card className="rounded-sm border-border overflow-hidden" data-testid="due-diligence-section">
        <div className="px-6 py-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm uppercase tracking-wider text-muted-foreground">{t("due_diligence_title")}</h3>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" hidden onChange={onDdFile} data-testid="dd-file-input" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}
                   data-testid="dd-upload-btn" className="rounded-sm gap-2">
              <Upload className="h-4 w-4" /> {uploading ? "…" : t("upload_document")}
            </Button>
            <Button size="sm" onClick={() => analyzeDd.mutate()} disabled={analyzeDd.isPending || ddDocuments.length === 0}
                   data-testid="dd-analyze-btn" className="rounded-sm">
              {analyzeDd.isPending ? "…" : t("analyze")}
            </Button>
          </div>
        </div>

        {ddDocuments.length > 0 && (
          <div className="px-6 py-3 border-b border-border flex flex-wrap gap-2">
            {ddDocuments.map((d) => (
              <Badge key={d.id} variant="secondary" className="rounded-sm gap-2 pr-1">
                {d.original_filename}
                <button onClick={() => removeDoc.mutate(d.id)} data-testid={`dd-remove-doc-${d.id}`}
                       className="hover:text-[hsl(var(--destructive))]">
                  <Trash2 className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {ddReports.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6">{t("dd_empty")}</p>
        ) : (
          <div className="divide-y divide-border">
            {ddReports.map((r) => {
              const meta = DD_SEVERITY_META[r.risk_level] || DD_SEVERITY_META.ok;
              const Icon = meta.icon;
              return (
                <div key={r.id} className="px-6 py-4" data-testid={`dd-report-${r.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4" style={{ color: meta.color }} />
                      <span className="text-sm font-medium">{fmtDate(r.created_at)}</span>
                      <Badge variant="outline" className="rounded-sm text-[10px]"
                            style={{ color: meta.color, borderColor: meta.color }}>
                        {t(`dd_risk_${r.risk_level}`)}
                      </Badge>
                    </div>
                    <Button variant="ghost" size="icon" data-testid={`dd-pdf-${r.id}`}
                           onClick={() => window.open(`${API}/due-diligence/reports/${r.id}/pdf`, "_blank")}>
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{r.summary}</p>
                  <div className="mt-2 space-y-1">
                    {r.findings.map((f, i) => {
                      const fMeta = DD_SEVERITY_META[f.severity] || DD_SEVERITY_META.ok;
                      const FIcon = fMeta.icon;
                      return (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <FIcon className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: fMeta.color }} />
                          <span>{f.text}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-popover">
          <DialogHeader><DialogTitle className="font-display text-2xl">{t("edit_property")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 space-y-2"><Label>{t("name")}</Label>
              <Input data-testid="edit-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" /></div>
            <div className="col-span-2 space-y-2"><Label>{t("address")}</Label>
              <Input data-testid="edit-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm" /></div>
            <div className="space-y-2"><Label>{t("area")}</Label>
              <Input type="number" value={form.area || ""} onChange={(e) => setForm({ ...form, area: e.target.value })} className="rounded-sm" /></div>
            <div className="space-y-2"><Label>{t("rooms")}</Label>
              <Input type="number" value={form.rooms || ""} onChange={(e) => setForm({ ...form, rooms: e.target.value })} className="rounded-sm" /></div>
            <div className="space-y-2"><Label>{t("purchase_price")}</Label>
              <Input type="number" value={form.purchase_price || ""} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className="rounded-sm" /></div>
            <div className="space-y-2"><Label>{t("current_value")}</Label>
              <Input type="number" data-testid="edit-current" value={form.current_value || ""} onChange={(e) => setForm({ ...form, current_value: e.target.value })} className="rounded-sm" /></div>
            <div className="col-span-2 space-y-2"><Label>{t("region")}</Label>
              <Select value={form.region || ""} onValueChange={(v) => setForm({ ...form, region: v })}>
                <SelectTrigger data-testid="edit-region" className="rounded-sm"><SelectValue placeholder={t("region_placeholder")} /></SelectTrigger>
                <SelectContent className="bg-popover">
                  {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)} className="rounded-sm">{t("cancel")}</Button>
            <Button onClick={saveEdit} data-testid="edit-save" disabled={update.isPending} className="rounded-sm">{t("save_changes")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
